import { LOG_LEVEL_ORDER } from './constants';

import type { LogEntry, LogLevel, LoggerPlugin } from './types';

const meetsMinLevel = (level: LogLevel, minLevel?: LogLevel): boolean =>
  !minLevel || LOG_LEVEL_ORDER[level] >= LOG_LEVEL_ORDER[minLevel];

export type Enhancer = () =>
  | Promise<Record<string, unknown>>
  | Record<string, unknown>;

const plugins: LoggerPlugin[] = [];
const enhancers: Enhancer[] = [];

export const loggerCore = {
  use(plugin: LoggerPlugin) {
    if (!plugins.some(p => p.name === plugin.name)) {
      plugins.push(plugin);
      plugin.init?.();
    }
  },

  useEnhancer(fn: Enhancer) {
    enhancers.push(fn);
  },

  getEnhancers(): Enhancer[] {
    return enhancers;
  },

  async flush() {
    await Promise.allSettled(plugins.map(p => p.flush?.()));
  },

  teardown() {
    for (const plugin of plugins) {
      try {
        plugin.teardown?.();
      } catch (error) {
        const typedError = error as Error & { digest?: string };
        console.warn('[logger] plugin error:', typedError.message);
      }
    }
  },

  setUser(user: { id: string; email?: string }) {
    for (const plugin of plugins) {
      try {
        plugin.setUser?.(user);
      } catch (error) {
        const typedError = error as Error & { digest?: string };
        console.warn('[logger] plugin error:', typedError.message);
      }
    }
  },

  async logEntry(entry: LogEntry) {
    for (const plugin of plugins) {
      if (!meetsMinLevel(entry.level, plugin.minLevel)) continue;
      try {
        await plugin.handle(entry);
      } catch (error) {
        const typedError = error as Error & { digest?: string };
        console.warn('[logger] plugin error:', typedError.message);
      }
    }
  },
};
